import {AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {NgbPopoverConfig} from "@ng-bootstrap/ng-bootstrap";
import {ActivatedRoute} from "@angular/router";
import {UserSessionService} from "../../services/user-session.service";
import {CloudApiService} from "../../services/cloud-api.service";
import {Subscription} from "rxjs";
import {BranchDto} from "../../services/dtos/branch.dto";
import {DashboardTerminalDto} from "../../services/dtos/dashboard-terminal.dto";
import {ConfigDto} from "../../services/dtos/config.dto";
import {ApplicationDto} from "../../services/dtos/application.dto";
import {DatePipe} from '@angular/common'
import {DeploymentDto} from "../../services/dtos/deployment.dto";
import {FormControl} from "@angular/forms";
import {RootDeploymentNode} from "./deployment-tree/root-deployment-node";
import {DeploymentUtilitiesService} from "../../services/deployment-utilities.service";
import {TreeNode} from "../../services/dtos/interfaces/tree-node";
import {ConfigLevelType} from "../../services/dtos/enums/config-level-type.enum";
import {UtilitiesService} from "../../services/utilities.service";
import {SmartLibraryApplication} from "../../services/dtos/enums/smart-library-application.enum";
import { ConfigLevel } from "../../services/dtos/interfaces/config-level";
import { NgSelectComponent } from '@ng-select/ng-select';

@Component({
    selector: 'deployment',
    template: require('./deployment.component.html'),
    styles: [require('./deployment.component.css')],
    providers: [NgbPopoverConfig]
})

export class DeploymentComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('versionSelect') versionSelect: NgSelectComponent;
    @ViewChild('deploymentTreeParent')deploymentTreeParent: ElementRef;
    @ViewChild('deploymentTreeRefresh')deploymentTreeRefresh: ElementRef;

    rootNode: RootDeploymentNode = new RootDeploymentNode();
    errorMessage: string;

    currentDateTime: string;
    specificDateTime: string;
    scheduleOption: string = 'now';

    latestVersion: string = "";
    highestInstalledVersion: string = "";
    availableVersions: string[] = [];
    versionControl: FormControl = new FormControl();

    restartAfterUpdate: boolean = true;

    downloadErrors: { [version: string]: string } = {};
    downloadLoading: { [row: string]: boolean } = {};

    pendingDeployments: DeploymentDto[] = [];
    recentDeployments: DeploymentDto[] = [];

    allTerminals: DashboardTerminalDto[] = [];

    private _isDeploymentTreeLoading: boolean;
    private _librarySubscription: Subscription;

    private _versionInputListenerAdded = false;
    private _versionInputLastTypedValue: string = null;

    constructor(
        private route: ActivatedRoute,
        private userSession: UserSessionService,
        private cloudApiService: CloudApiService,
        private deploymentUtilitiesService: DeploymentUtilitiesService,
        private utilitiesService: UtilitiesService,
        private datePipe: DatePipe
    ) {
    }

    ngOnInit(): void {
        this.currentDateTime = this.datePipe.transform(Date.now(), 'yyyy-MM-ddTHH:mm');
        this.specificDateTime = this.currentDateTime;

        this._librarySubscription = this.userSession.library.subscribe(library => {
            if (library) {
                this.loadAvailableVersions();
                this.loadHierarchy();
            }
        });
    }

    ngAfterViewInit() : void {
        this._librarySubscription = this.userSession.library.subscribe(library => {
            if (library) {
                this.loadPendingDeployments();
                this.loadRecentDeployments();
            }
        })
    }

    ngOnDestroy(): void {
        this._librarySubscription.unsubscribe();
    }

    loadPendingDeployments(): void {
        this.setDeploymentTreeLoadState(true);
        this.cloudApiService.getPendingDeployments().subscribe(
            pendingDeployments => {
                this.pendingDeployments = pendingDeployments;
                this.setDeploymentTreeLoadState(false);
            }
        )
    }

    loadRecentDeployments(): void {
        this.cloudApiService.getRecentDeployments().subscribe(
            recentDeployments => {
                this.recentDeployments = recentDeployments;
            }
        )
    }

    setDeploymentTreeLoadState(isLoading: boolean) {
        if (this._isDeploymentTreeLoading == isLoading) return
        this._isDeploymentTreeLoading = isLoading;

        if (this.deploymentTreeParent == null ||
            this.deploymentTreeParent.nativeElement == null ||
            this.deploymentTreeRefresh == null ||
            this.deploymentTreeRefresh.nativeElement == null)
            return;

        if (isLoading) {
            this.deploymentTreeParent.nativeElement.classList.add('disabled-div');
            this.deploymentTreeParent.nativeElement.parentElement.classList.add('disabled-li');
            this.deploymentTreeRefresh.nativeElement.classList.add('disabled-button');
        } else {
            this.deploymentTreeParent.nativeElement.classList.remove('disabled-div');
            this.deploymentTreeParent.nativeElement.parentElement.classList.remove('disabled-li');
            this.deploymentTreeRefresh.nativeElement.classList.remove('disabled-button');
        }
    }

    downloadVersion(version: string, row: string) {
        delete this.downloadErrors[version];
        this.downloadLoading[row] = true;
        this.cloudApiService.getDownloadUrl(version).subscribe(urlDto => {
            this.downloadLoading[row] = false;
            if (urlDto.error) {
                this.downloadErrors[version] = urlDto.error;
            } else {
                window.location.href = urlDto.url;
            }
        });
    }

    onVersionDropdownOpen() {
        const current = this.versionControl.value;
        setTimeout(() => {
            const input = this.versionSelect.elementRef.nativeElement.querySelector('input');
            if (input) {
                if (current) {
                    input.value = current;
                    input.focus();
                    input.dispatchEvent(new Event('input', { bubbles: true }));
                }
                if (!this._versionInputListenerAdded) {
                    input.addEventListener('mousedown', (e: Event) => e.stopPropagation());

                    input.addEventListener('input', () => {
                        if (input.value) {
                            this._versionInputLastTypedValue = input.value;
                        }
                    });

                    input.addEventListener('blur', () => {
                        if (this._versionInputLastTypedValue) {
                            this.versionControl.setValue(this._versionInputLastTypedValue);
                            this._versionInputLastTypedValue = null;
                        }
                    });

                    this._versionInputListenerAdded = true;
                }
            }
        });
    }

    onVersionSelect() {
        this._versionInputLastTypedValue = null; // discard typed value, keep the clicked selection
        const input = this.versionSelect.elementRef.nativeElement.querySelector('input');
        if (input) {
            input.blur();
        }
    }

    noFilter = () => true;

    refresh(): void {
        this.setDeploymentTreeLoadState(true);
        this.refreshApplications();
        this.loadRecentDeployments();
        this.loadPendingDeployments();
    }

    private loadAvailableVersions(): void {
        this.cloudApiService.getApplicationVersionData().subscribe(
            versionData => {
                this.availableVersions = versionData;
                if (versionData.length > 0) {
                    this.latestVersion = versionData[0];
                }
            }
        )
    }

    private loadHierarchy(): void {
        this.cloudApiService.getConfigHierarchy().subscribe(
            hierarchy => {
                this.highestInstalledVersion = hierarchy.maxVersion;

                this.allTerminals = [];
                let configs = hierarchy.configs.map(x => Object.assign(new ConfigDto(), x));
                for (let config of configs) {
                    config.branches = config.branches.map(x => Object.assign(new BranchDto(), x));
                    for (let branch of config.branches) {
                        branch.terminals = branch.terminals.map(x => Object.assign(new DashboardTerminalDto(), x));
                        for (let terminal of branch.terminals) {
                            this.allTerminals.push(terminal);
                            terminal.applications = terminal.applications.map(x => Object.assign(new ApplicationDto(), x));
                        }
                    }
                }

                this.rootNode = new RootDeploymentNode();
                this.rootNode.setChildren(configs);
            }
        )
    }

    private levelCanUseCloudDeployments(level: ConfigLevel) : boolean {
        if (level.configLevel == ConfigLevelType.Terminal) {
            let services = level.getChildren().find(x => this.deploymentUtilitiesService.nodeAsApplication(x).application == SmartLibraryApplication.Services);
            level.canUseCloudDeployments =
                (services != null && this.deploymentUtilitiesService.canUseCloudDeployments(this.deploymentUtilitiesService.nodeAsApplication(services).version));

            return level.canUseCloudDeployments;
        }

        level.canUseCloudDeployments = level.getChildren().some(x => this.levelCanUseCloudDeployments(this.deploymentUtilitiesService.nodeAsLevel(x)))
        return level.canUseCloudDeployments;
    }

    private refreshApplications() : void {
        this.cloudApiService.getDeploymentApplications().subscribe(
            applications => {
                let nodes: TreeNode[] = [this.rootNode];
                while (nodes.length > 0) {
                    let node = nodes.pop();

                    if (node.configLevel == ConfigLevelType.Terminal) {
                        let applicationsForNode = applications
                            .filter(x => x.terminalId == this.deploymentUtilitiesService.nodeAsTerminal(node).id)
                            .map(x => new ApplicationDto().fromObject(x));

                        node.setChildren(applicationsForNode);
                    } else {
                        nodes.push(...node.getChildren());
                    }
                }

                // services may have been uninstalled or reverted to an incompatible version
                // check if our configs / branches / terminals can still use cloud deployments
                for (let child of this.rootNode.getChildren()) {
                    this.levelCanUseCloudDeployments(this.deploymentUtilitiesService.nodeAsLevel(child));
                }
            }
        )
    }

}


// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/app/components/deployment/deployment.component.ts